import { Injectable } from "@angular/core";
import {
  CartTotal,
  Product,
  ResponseTotal,
  SaleItem,
} from "@tendercuts/models";
import { PosQuote, ValidateCouponCode } from "src/models";

@Injectable({ providedIn: "root" })
export class CreateQuoteService {
  /**
   * manages quantity of a product
   *
   * @param {SaleItem[]} products
   */
  private async updateCartProducts(
    oldProducts: SaleItem[],
    product: Product,
    qtyToAdd,
    isItemRemove: boolean
  ) {
    let productIndex = -1;
    let quantity = 0;

    oldProducts.find((item: SaleItem, index) => {
      // A product already exists, so extract the quantity and note down index
      if (item.product.id === product.id) {
        quantity = item.quantity;
        productIndex = index;
        return true;
      }

      return false;
    });
    // if a old Item exists nuke it and create a new one
    if (productIndex !== -1) {
      oldProducts.splice(productIndex, 1);
    }

    if (quantity + qtyToAdd > 0 && !isItemRemove) {
      const item = SaleItem.buildFromProduct(product, quantity + qtyToAdd);
      oldProducts.push(item);
    }
    return oldProducts;
  }

  /**
   * 1. Fetch the totals using quote products
   * 2. return the totals object
   * 3. Calculate earned rewards using the non carry bag cart products.
   *    Customer is an elite 5X reward points adding
   *    Non elite customer adding 1X reward points.
   * @param quote
   * @param response
   * @param isCouponApplied
   */
  private computeTotals(quote: PosQuote) {
    const newTotal: ResponseTotal = new ResponseTotal();
    newTotal.rewardsSpend = quote.rewardPointsSpend
      ? quote.rewardPointsSpend
      : 0;
    newTotal.discount = quote.couponTotalDiscount;
    quote.cartProducts.forEach((item: SaleItem) => {
      const itemTaxAmount = +(
        item.rowTotalWithDiscount *
        (item.taxPercent / 100)
      ).toFixed(2);
      item.taxAmount = itemTaxAmount ? itemTaxAmount : 0;
      newTotal.subtotal += item.rowTotal;
      newTotal.tax_amount += item.taxAmount;
      newTotal.grand_total += item.rowTotal;
      newTotal.eliteSavingsPos += item.eliteSavings;
    });

    newTotal.tax_amount = Math.floor(newTotal.tax_amount);
    newTotal.grand_total += newTotal.tax_amount;
    newTotal.grand_total -= quote.rewardPointsSpend
      ? quote.rewardPointsSpend
      : 0;
    newTotal.grand_total -= quote.couponTotalDiscount;

    return CartTotal.fromTotals(newTotal);
  }

  /**
   * Re build the sale item for entire cart products to apply
   * elite price for elite cart.
   * Normal price for non elite cart
   * @param cartProducts - Sale items in cart
   * @param isEliteComputation - Determine to apply elite price or not
   * @returns
   */
  reBuildSaleItems(
    cartProducts: SaleItem[],
    isEliteComputation: boolean
  ): SaleItem[] {
    cartProducts.forEach((item: SaleItem, index) => {
      cartProducts[index] = SaleItem.buildFromProduct(
        item.product,
        item.quantity,
        isEliteComputation
      );
    });

    return cartProducts;
  }

  /**
   * check for elite quote or elite customer and update the cart
   * sale items and compute totals accordingly
   * @param product - Porduct to be added to the cart
   * @param weight - Weight of the product added to the cart
   * @param quote - current pos quote
   * @returns - Updated the POSQuote mode
   */
  addProductsToCart(
    product: Product,
    weight: number,
    quote: PosQuote
  ): PosQuote {
    this.updateCartProducts(quote.cartProducts, product, weight, false);
    const newQuote: PosQuote = quote;
    newQuote.cartProducts = this.reBuildSaleItems(
      newQuote.cartProducts,
      newQuote.isElite
    );
    newQuote.totals = this.computeTotals(quote);

    return newQuote;
  }

  /**
   * check for elite quote or elite customer and update the cart
   * sale items and compute totals accordingly
   * @param product - Porduct to be removed from the cart
   * @param quote - current pos quote
   * @returns - Updated the POSQuote mode
   */
  removeProductsFromCart(product: Product, quote: PosQuote): PosQuote {
    const newProducts = quote.cartProducts;
    this.updateCartProducts(newProducts, product, -1, true);
    const newQuote: PosQuote = quote;
    newQuote.cartProducts = newProducts;
    newQuote.cartProducts = this.reBuildSaleItems(
      newQuote.cartProducts,
      newQuote.isElite
    );
    newQuote.totals = newQuote.totals = this.computeTotals(quote);

    return newQuote;
  }

  /**
   * Set the rewardPointsSpend and compute the total
   * @param points - Points to be applied to quote
   * @param quote - Current pos quote
   * @returns - Updated PosQuote with reward points applied
   */
  applyRewardPoints(points: number, quote: PosQuote): PosQuote {
    const newQuote: PosQuote = quote;
    newQuote.rewardPointsSpend = points;
    newQuote.totals = this.computeTotals(newQuote);

    return newQuote;
  }

  /**
   * Remove the rewardPointsSpend and compute the total
   * @param points - Points to be removed from to quote
   * @param quote - Current pos quote
   * @returns - Updated PosQuote with removed reward points
   */
  removeRewardPoints(points: number, quote: PosQuote): PosQuote {
    const newQuote: PosQuote = quote;
    newQuote.rewardPointsSpend = 0;
    newQuote.totals = this.computeTotals(newQuote);

    return newQuote;
  }

  /**
   * set the couponTotalDiscount and then compute the price
   * @param validateCouponCode - ValideCouponCode is the response from the server
   * @param couponCode - Coupon code entered by the user
   * @param quote - Current quote in cart state
   * @returns updated PosQuote with computations of applying coupon code
   */
  applyCoupon(
    validateCouponCode: ValidateCouponCode,
    couponCode: string,
    quote: PosQuote
  ): PosQuote {
    const newQuote: PosQuote = quote;
    newQuote.cartProducts.forEach((item: SaleItem) => {
      let itemDiscount = validateCouponCode
        ? validateCouponCode.items[item._product.id]
        : 0;
      item.discountAmount = +itemDiscount;
    });
    newQuote.couponTotalDiscount = validateCouponCode
      ? validateCouponCode.discountAmount
      : 0;
    newQuote.totals = this.computeTotals(newQuote);
    newQuote.couponCode = couponCode;

    return newQuote;
  }

  /**
   * Removes the couponTotalDiscount and then compute the price
   * @param quote - Current quote in the cart state
   * @returns updated PosQuote after removing the coupon code and
   * discounts
   */
  removeCoupon(quote: PosQuote): PosQuote {
    const newQuote: PosQuote = quote;
    newQuote.cartProducts.forEach((item: SaleItem) => {
      item.discountAmount = 0;
    });
    newQuote.couponTotalDiscount = 0;
    newQuote.totals = this.computeTotals(newQuote);
    newQuote.couponCode = null;

    return newQuote;
  }

  /**
   * Just rebuild the sale items and re compute the totals
   * @param quote POS QUOTE
   * @returns
   */
  refreshQute(quote: PosQuote) {
    const newQuote: PosQuote = quote;
    newQuote.cartProducts = this.reBuildSaleItems(
      quote.cartProducts,
      quote.isElite
    );
    newQuote.totals = this.computeTotals(newQuote);

    return newQuote;
  }
}
